import { useState, useMemo, FormEvent } from 'react';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe, type SetupIntent } from '@stripe/stripe-js';
import { toast } from 'react-toastify';
import { Stack, Box, useTheme } from '@mui/material';
import type Stripe from 'stripe';
import SmallModal from '../../global/SmallModal';
import CheckoutElementsForm from '../../payments/CheckoutElementsForm';
import { STRIPE_PK } from '../../../constants/stripe';

const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

interface Props {
  open: boolean;
  onClose: () => void;
  setupIntent: Stripe.SetupIntent;
  onSuccess: (result: SetupIntent) => void | Promise<void>;
  title?: string;
  buttonText?: string;
}

function PaymentMethodForm({
  onSuccess,
  onClose,
  setupIntentClientSecret,
  open,
  title,
  buttonText,
}: {
  onSuccess: (result: SetupIntent) => void | Promise<void>;
  onClose: () => void;
  setupIntentClientSecret: string;
  open: boolean;
  title?: string;
  buttonText?: string;
}) {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!stripe || !elements) {
      toast.error('Stripe is not initialized. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const submission = await elements.submit();
      if (submission.error) {
        toast.error(submission.error.message);
        setIsSubmitting(false);
        return;
      }

      const cardElement = elements.getElement('card');
      if (!cardElement) {
        toast.error('Card element not found. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const result = await stripe.confirmCardSetup(setupIntentClientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error) {
        toast.error(result.error.message);
        setIsSubmitting(false);
        return;
      }

      if (result.setupIntent) {
        await onSuccess(result.setupIntent);
      }

      onClose();
    } catch (error) {
      toast.error('Failed to add payment method. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      width={440}
      title={title || 'Add Payment Method'}
      footer={{
        button: {
          text: buttonText || 'Add Payment Method',
          disabled: isSubmitting || !stripe,
          loading: isSubmitting,
          type: 'button',
          onClick: handleSubmit,
        },
      }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}>
        <form
          onSubmit={handleSubmit}
          id="payment-method-form"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            paddingBlock: theme.spacing(2),
          }}>
          <Stack
            p={2}
            width="100%"
            gap={2}
            boxSizing="border-box"
            sx={{
              maxWidth: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}>
            <CheckoutElementsForm disabled={isSubmitting} disableLink={false} />
          </Stack>
        </form>
      </Box>
    </SmallModal>
  );
}

function PaymentMethodCollectionModal({
  open,
  onClose,
  setupIntent,
  onSuccess,
  title,
  buttonText,
}: Props) {
  const options = useMemo(
    () => ({
      clientSecret: setupIntent.client_secret || undefined,
    }),
    [setupIntent.client_secret],
  );

  if (!stripePromise || !setupIntent.client_secret) {
    return null;
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentMethodForm
        open={open}
        onSuccess={onSuccess}
        onClose={onClose}
        setupIntentClientSecret={setupIntent.client_secret}
        title={title}
        buttonText={buttonText}
      />
    </Elements>
  );
}

export default PaymentMethodCollectionModal;
